import styled from "styled-components";

const INK = "#002e3a";
const MUTED = "#36525a";
const FAINT = "#4a6168";

export const Card = styled.section`
  margin: 4px 0 12px;
  padding: 14px;
  border-radius: 16px;
  background: #e8f1f2;
  color: ${INK};
  font-family: inherit;
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* English only, so left to right also inside the Arabic layout. */
  direction: ltr;
  text-align: left;
`;

export const Top = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const Logo = styled.img`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
`;

export const IconBox = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
  background: #ffffff;
  color: ${(props) => props.color || INK};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Texts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: ${INK};
`;

export const Body = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.35;
  color: ${MUTED};
`;

export const Panel = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: #ffffff;
  font-size: 14px;
  line-height: 1.45;

  ol {
    margin: 0;
    padding-left: 20px;
  }

  p {
    margin: 0;
  }

  p + p {
    margin-top: 6px;
  }
`;

export const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

const BaseButton = styled.button`
  min-height: 44px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:focus-visible {
    outline: 3px solid #5fa8b8;
    outline-offset: 2px;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  flex-grow: 1;
  border: 0;
  background: ${INK};
  color: #ffffff;
  font-weight: 700;

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`;

export const SecondaryButton = styled(BaseButton)`
  padding: 0 16px;
  border: 1px solid #9fb8bd;
  background: transparent;
  color: ${INK};
  font-weight: 500;
`;

export const Note = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${FAINT};
`;

// The iPhone guide, a sheet over the bottom of the screen.

export const Backdrop = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  background: rgba(0, 23, 29, 0.55);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

export const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 22px 22px 0 0;
  padding: 10px 20px calc(24px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: ${INK};
  font-family: "Roboto", "Tajawal", sans-serif;
  /* English only, so left to right also inside the Arabic layout. */
  direction: ltr;
  text-align: left;

  &:focus {
    outline: none;
  }
`;

export const Handle = styled.div`
  align-self: center;
  width: 40px;
  height: 5px;
  border-radius: 3px;
  background: #c7d1d3;
`;

export const SheetTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  color: ${INK};
`;

export const Steps = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Step = styled.li`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 14px;
  border-radius: 14px;
  background: #f2f6f7;
`;

export const StepNumber = styled.div`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${INK};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
`;

export const StepText = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StepTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 1.3;
`;

export const StepHint = styled.div`
  font-size: 13px;
  line-height: 1.35;
  color: ${FAINT};
`;

export const StepIcon = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #d5dfe1;
  color: ${(props) => props.color || "#1c2b2f"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StepLogo = styled.img`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 10px;
`;

export const SheetButton = styled(PrimaryButton)`
  flex-grow: 0;
  min-height: 50px;
  border-radius: 12px;
  font-size: 16px;
`;
